/** LegalPage — premium reader for Privacy/Terms/Refund.
 *  CMS-driven via /api/legal/{key}. Falls back to the static typographic template
 *  when admin hasn't authored body_html yet.
 */
import { useEffect, useState } from "react";
import { Container, Eyebrow, Section, PageHeader, Breadcrumb, LoadingState } from "../components/ui";
import { useRevealOnScroll } from "../lib/useRevealOnScroll";
import { api } from "../lib/api";

export function LegalPage({ title, intro, sections = [], updated = "Updated · January 2026" }) {
  const [active, setActive] = useState(sections[0]?.id);
  useRevealOnScroll();

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => { if (en.isIntersecting) setActive(en.target.id); }),
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [sections]);

  return (
    <Section padded="sm" data-testid="legal-page">
      <Container>
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Legal", to: "/" }, { label: title }]} />
        <PageHeader eyebrow={updated} title={title} lede={intro} className="mt-6" />
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12">
          <aside className="lg:sticky lg:top-32 self-start" data-testid="legal-toc">
            <Eyebrow>Contents</Eyebrow>
            <ol className="mt-5 flex flex-col gap-1.5 text-[13.5px]">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} data-testid={`legal-toc-link-${s.id}`}
                    className={`block py-1.5 transition-colors border-l-2 pl-3 ${active === s.id ? "border-mauve text-text font-medium" : "border-transparent text-text-dim hover:text-text"}`}>
                    <span className="font-mono text-text-faint mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>
          <article className="prose-nvl max-w-[68ch]">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="mb-14 scroll-mt-32 reveal" data-delay={i % 5}>
                <h2 className="font-display text-[clamp(26px,2.6vw,34px)] tracking-[-0.01em] mb-4">
                  <span className="text-text-faint font-mono text-[15px] mr-3 align-middle">{String(i + 1).padStart(2, "0")}</span>
                  {s.label}
                </h2>
                <div className="space-y-4 text-[15.5px] leading-[1.85] text-text-dim">
                  {s.body.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </section>
            ))}
          </article>
        </div>
      </Container>
    </Section>
  );
}

function CmsLegalPage({ cmsKey, fallback }) {
  const [cms, setCms] = useState(null);
  useEffect(() => {
    api.get(`/legal/${cmsKey}`).then((r) => setCms(r.data)).catch(() => setCms({ body_html: "" }));
  }, [cmsKey]);
  useRevealOnScroll();
  if (!cms) return <Section padded="sm"><Container><LoadingState /></Container></Section>;

  if (cms.body_html && cms.body_html.trim().length > 0) {
    return (
      <Section padded="sm" data-testid="legal-page">
        <Container>
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Legal", to: "/" }, { label: cms.title || fallback.title }]} />
          <PageHeader
            eyebrow={cms.published_at ? `Updated · ${new Date(cms.published_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}` : (fallback.updated || "Updated · January 2026")}
            title={cms.title || fallback.title}
            className="mt-6"
          />
          <article className="prose-nvl max-w-[78ch] mt-8 text-[15.5px] leading-[1.85] text-text-dim"
            data-testid="legal-cms-body" dangerouslySetInnerHTML={{ __html: cms.body_html }} />
        </Container>
      </Section>
    );
  }
  return <LegalPage {...fallback} />;
}

const PRIVACY_SECTIONS = [
  { id: "scope", label: "Scope of this policy", body: [
    "This Privacy Policy applies to all visitors, students, customers, researchers, instructors and administrators of the Nikhil Vatika Lab (NVL) platform — including the website, mobile applications, services and any related communication.",
    "By using NVL you consent to the practices described here. Where local laws grant you stronger protections, those laws will always prevail.",
  ]},
  { id: "information-we-collect", label: "Information we collect", body: [
    "We collect only what is necessary to operate the platform responsibly: your name, email address, country, payment details (handled by certified payment processors), and the content you choose to share with us (such as enrolment, sadhana progress and consultation notes).",
    "We never sell your personal data. We never share it for advertising. We never use it for purposes other than operating and improving NVL.",
  ]},
  { id: "how-we-use", label: "How we use your information", body: [
    "We use your information to deliver courses you enrol in, dispatch products you order, schedule services you book, communicate updates relevant to your study, and maintain a secure account environment for you.",
    "We may also use aggregate, non-identifying signals to understand which areas of the platform are most useful and to plan future modules accordingly.",
  ]},
  { id: "your-rights", label: "Your rights", body: [
    "You may request access to your data, correct inaccuracies, delete your account, export your information, or withdraw consent at any time by writing to privacy@nikhilvatikalab.com.",
    "All such requests are processed within a reasonable timeframe and at no cost to you.",
  ]},
  { id: "security", label: "Security", body: [
    "We protect your information with industry-standard encryption in transit and at rest, strict access controls within our team, regular security reviews, and prompt response to any concerns you raise.",
    "No system is perfect — but our commitment to your safety is absolute.",
  ]},
  { id: "contact", label: "Contact us", body: [
    "For any privacy matter, please write to privacy@nikhilvatikalab.com or admissions@nikhilvatikalab.com. We respond personally and promptly.",
  ]},
];

const TERMS_SECTIONS = [
  { id: "acceptance", label: "Acceptance of terms", body: [
    "By accessing or using NVL you agree to be bound by these terms. If you do not agree, please refrain from using the platform.",
  ]},
  { id: "accounts", label: "Accounts & access", body: [
    "You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
    "Some advanced sadhanas and research areas are restricted by initiation or institutional access. Such restrictions are part of our commitment to lineage and academic integrity.",
  ]},
  { id: "intellectual-property", label: "Intellectual property", body: [
    "All content on the platform — courses, recordings, written material, research, translations, illustrations, software — is the property of NVL or licensed to NVL. You receive a personal, non-transferable licence to access it for your own study.",
  ]},
  { id: "conduct", label: "Conduct", body: [
    "We expect respect, courtesy and intellectual honesty from every member. Harassment, plagiarism, the unauthorised redistribution of protected material, or any attempt to undermine the platform will result in revocation of access.",
  ]},
  { id: "liability", label: "Liability", body: [
    "Spiritual practice has profound effects. We provide instruction with care; however, you are responsible for the practices you undertake and the decisions you make. Consult qualified professionals for medical, legal or financial matters.",
  ]},
  { id: "changes", label: "Changes to these terms", body: [
    "We may update these terms occasionally to reflect operational, legal or educational changes. Material changes will be communicated clearly and in advance.",
  ]},
];

const REFUND_SECTIONS = [
  { id: "courses", label: "Courses", body: [
    "If a course does not meet your expectations, you may request a full refund within 7 days of enrolment, provided you have completed less than 25% of the course content.",
    "Bespoke courses, lineage initiations and small-cohort programs are non-refundable once admission has been confirmed, as they reserve a place that could otherwise have gone to another seeker.",
  ]},
  { id: "products", label: "Products", body: [
    "Consecrated and energised items are returnable only if they arrive damaged, defective, or materially different from the description, and only when reported within 72 hours of delivery with photographic evidence.",
    "All other products may be returned within 7 days of delivery in original, unopened condition.",
  ]},
  { id: "services", label: "Services & consultations", body: [
    "Consultations may be rescheduled free of charge with at least 24 hours' notice. Cancellations within 24 hours of the appointment are non-refundable.",
    "If our expert is unable to attend, the consultation is rescheduled or refunded at your option.",
  ]},
  { id: "library", label: "Library subscriptions", body: [
    "Annual library subscriptions are refundable on a pro-rata basis within the first 30 days. After 30 days, no refunds are issued, although the subscription continues until its natural expiry.",
  ]},
  { id: "how-to-request", label: "How to request a refund", body: [
    "Write to refunds@nikhilvatikalab.com from the email address registered to your NVL account, including your order/enrolment reference and a short note explaining your situation.",
    "Refunds are processed to the original payment method within 7-10 business days of approval.",
  ]},
];

export function PrivacyPolicyPage() {
  return <CmsLegalPage cmsKey="privacy" fallback={{
    title: "Privacy Policy",
    intro: "Your trust is the foundation of our work. This policy explains, in plain language, what information NVL collects, why we collect it, how we use it, and the rights you retain at every stage.",
    sections: PRIVACY_SECTIONS,
  }} />;
}

export function TermsConditionsPage() {
  return <CmsLegalPage cmsKey="terms" fallback={{
    title: "Terms & Conditions",
    intro: "These terms describe the agreement between you and Nikhil Vatika Lab. They are written to be clear, fair and easy to understand.",
    sections: TERMS_SECTIONS,
  }} />;
}

export function RefundPolicyPage() {
  return <CmsLegalPage cmsKey="refund-policy" fallback={{
    title: "Refund Policy",
    intro: "We want you to feel completely confident about your choice to study with NVL. This policy explains when and how refunds are issued.",
    sections: REFUND_SECTIONS,
  }} />;
}

export function AccessibilityPage() {
  return <CmsLegalPage cmsKey="accessibility" fallback={{
    title: "Accessibility",
    intro: "We strive to make NVL usable by everyone, regardless of ability. This page documents our commitment and current state of conformance.",
    sections: [
      { id: "commitment", label: "Our commitment", body: [
        "NVL aims for WCAG 2.1 AA compliance across all public pages. Where we fall short, we treat the gap as a bug and fix it.",
      ]},
      { id: "features", label: "Accessibility features", body: [
        "Keyboard-navigable controls, high-contrast theme, focus-visible outlines, screen-reader-friendly landmarks and headings, and resizable typography are built into every page.",
      ]},
      { id: "feedback", label: "Feedback", body: [
        "If you encounter a barrier on the platform, write to accessibility@nikhilvatikalab.com. We respond personally and will prioritise the fix.",
      ]},
    ],
  }} />;
}
